import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wrench, 
  Server, 
  Database, 
  Globe, 
  Mail, 
  Phone, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Hammer,
  MessageCircle,
  HelpCircle,
  Info,
  Shield,
  Zap,
  RefreshCw,
  Loader,
  User,
  Users,
  Calendar
} from 'lucide-react';
import { maintenancePageAPI } from '../services/api';

const Maintenance = () => {
  const [pageData, setPageData] = useState({
    title: 'We\'ll be back soon!',
    subtitle: 'We\'re currently performing some maintenance on our site.',
    description: 'We\'re working hard to improve your experience. Please check back soon.',
    estimatedTime: '2 hours',
    progress: 65,
    status: 'In Progress',
    services: [
      { name: 'Website', status: 'maintenance', icon: Globe },
      { name: 'Database', status: 'maintenance', icon: Database },
      { name: 'API Services', status: 'maintenance', icon: Server },
      { name: 'Payment System', status: 'online', icon: Globe }
    ],
    updates: [
      { time: '2:30 PM', message: 'Database optimization in progress' },
      { time: '2:15 PM', message: 'Security updates completed' },
      { time: '2:00 PM', message: 'Maintenance started' }
    ],
    helpTitle: 'Need Help?',
    emergencyContact: '+1 (555) 123-4567',
    mainIcon: 'Settings',
    showServiceStatus: false,
    estimatedTimeTitle: 'Estimated Time',
    progressTitle: 'Progress',
    recentUpdatesTitle: 'Recent Updates'
  });

  useEffect(() => {
    // Fetch page data from API
    const fetchPageData = async () => {
      try {
        const response = await maintenancePageAPI.getContent();
        if (response?.data?.success) {
          const apiData = response.data.data;
          
          // Map API data to component state
          setPageData({
            title: apiData.title || pageData.title,
            subtitle: apiData.subtitle || pageData.subtitle,
            description: apiData.description || pageData.description,
            estimatedTime: apiData.estimated_time_value || pageData.estimatedTime,
            progress: parseInt(apiData.progress_value?.replace('%', '')) || pageData.progress,
            status: 'In Progress',
            services: pageData.services, // Keep default services for now
            updates: apiData.recent_updates || pageData.updates,
            helpTitle: apiData.help_title || 'Need Help?',
            helpOptions: apiData.help_options || [],
            emergencyContact: pageData.emergencyContact,
            mainIcon: apiData.main_icon || 'Settings',
            showServiceStatus: apiData.show_service_status || false,
            estimatedTimeTitle: apiData.estimated_time_title || 'Estimated Time',
            progressTitle: apiData.progress_title || 'Progress',
            recentUpdatesTitle: apiData.recent_updates_title || 'Recent Updates'
          });
        }
      } catch (error) {
        console.error('Error fetching page data:', error);
      }
    };

    fetchPageData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'text-green-400 dark:text-green-300';
      case 'maintenance':
        return 'text-yellow-400 dark:text-yellow-300';
      case 'offline':
        return 'text-red-400 dark:text-red-300';
      default:
        return 'text-gray-400 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return CheckCircle;
      case 'maintenance':
        return Wrench;
      case 'offline':
        return XCircle;
      default:
        return AlertTriangle;
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const iconMap = {
      Settings: Wrench, // Map Settings to Wrench for backward compatibility
      Wrench,
      Server,
      Database,
      Globe,
      Mail,
      Phone,
      Clock,
      AlertTriangle,
      CheckCircle,
      XCircle,
      Hammer,
      MessageCircle,
      HelpCircle,
      Info,
      Shield,
      Zap,
      RefreshCw,
      Loader,
      User,
      Users,
      Calendar
    };
    return iconMap[iconName] || Wrench; // Fallback to Wrench
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      
      <main className="pt-16 lg:pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 pt-20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20 dark:from-blue-600/30 dark:to-purple-600/30"></div>
          <div className="absolute top-0 left-0 w-full h-full">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white/10 dark:bg-white/20 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -100, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center mb-12"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block mb-8"
            >
              {(() => {
                const IconComponent = getIconComponent(pageData.mainIcon);
                return <IconComponent className="w-20 h-20 text-blue-400 dark:text-blue-300 mx-auto" />;
              })()}
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {pageData.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-200 dark:text-blue-100 mb-6">
              {pageData.subtitle}
            </p>
            
            <p className="text-lg text-gray-300 dark:text-gray-200 max-w-2xl mx-auto">
              {pageData.description}
            </p>
          </motion.div>

          {/* Progress Bar */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="w-full max-w-md mb-12"
          >
            <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/20 dark:border-white/10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white font-semibold">{pageData.estimatedTimeTitle}</span>
                <span className="text-blue-400 dark:text-blue-300 font-bold">{pageData.estimatedTime}</span>
              </div>
              
              <div className="w-full bg-white/20 dark:bg-white/10 rounded-full h-3 mb-4">
                <motion.div
                  className="bg-gradient-to-r from-blue-400 to-purple-400 dark:from-blue-300 dark:to-purple-300 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${pageData.progress}%` }}
                  transition={{ duration: 2, delay: 0.5 }}
                />
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-300 dark:text-gray-200">{pageData.progressTitle}</span>
                <span className="text-blue-400 dark:text-blue-300 font-bold">{pageData.progress}%</span>
              </div>
            </div>
          </motion.div>

          {/* Service Status */}
          {pageData.showServiceStatus && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Service Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {pageData.services.map((service, index) => {
                  const StatusIcon = getStatusIcon(service.status);
                  const ServiceIcon = typeof service.icon === 'string' ? getIconComponent(service.icon) : (service.icon || Globe);
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
                      whileHover={{ scale: 1.02 }}
                      className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/20 dark:border-white/10"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <ServiceIcon className="w-6 h-6 text-blue-400 dark:text-blue-300" />
                          <span className="text-white font-semibold">{service.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusIcon className={`w-5 h-5 ${getStatusColor(service.status)}`} />
                          <span className={`text-sm font-medium ${getStatusColor(service.status)}`}>
                            {service.status === 'online' ? 'Online' : 
                             service.status === 'maintenance' ? 'Maintenance' : 'Offline'}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Updates */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-6">{pageData.recentUpdatesTitle || 'Recent Updates'}</h2>
            <div className="max-w-2xl mx-auto">
              {pageData.updates.map((update, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.8 }}
                  className="flex items-start gap-4 bg-white/5 dark:bg-white/5 backdrop-blur-sm rounded-lg p-4 mb-3 border border-white/10 dark:border-white/10"
                >
                  <div className="w-2 h-2 bg-blue-400 dark:bg-blue-300 rounded-full mt-2 flex-shrink-0"></div>
                                   <div className="flex-1 text-left">
                     <p className="text-gray-300 dark:text-gray-200 text-sm">{update.time}</p>
                     <p className="text-white">{update.text || update.message}</p>
                   </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

                   {/* Contact Information */}
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.9, duration: 0.8 }}
             className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-lg p-6 max-w-md mx-auto"
           >
             <h3 className="text-xl font-bold text-white mb-4">{pageData.helpTitle || 'Need Help?'}</h3>
             <div className="space-y-3 text-left">
               {/* Dynamic Help Options from API */}
               {pageData.helpOptions && pageData.helpOptions.length > 0 && pageData.helpOptions.map((option, index) => {
                 const IconComponent = getIconComponent(option.icon || 'Mail');
                 return (
                   <div key={index} className="flex items-center gap-3">
                     <IconComponent className="w-5 h-5 text-blue-400 dark:text-blue-300" />
                     <div>
                       <p className="text-white font-semibold">{option.title}</p>
                       <a href={option.link} className="text-blue-400 dark:text-blue-300 hover:text-blue-300 dark:hover:text-blue-200">
                         {option.value}
                       </a>
                     </div>
                   </div>
                 );
               })}
             </div>
           </motion.div>
        </div>
      </main>
      
    </div>
  );
};

export default Maintenance; 